import React, { useEffect, useMemo, useState } from 'react';

interface Lead {
    id: number;
    created_at: string;
    source: string;
    source_detail: string | null;
    email: string | null;
    phone: string | null;
    name: string | null;
    company: string | null;
    message: string | null;
    status: string;
    product_slug: string | null;
    utm_source: string | null;
    utm_campaign: string | null;
    utm_content: string | null;
}

interface Summary {
    total_leads: number;
    today: number;
    last_7_days: number;
    last_30_days: number;
    prompty_unlocks_total: number;
    by_source_30d: Array<{ source: string; source_detail: string | null; n: number }>;
    by_utm_content_30d: Array<{ utm_content: string; n: number }>;
}

const TEST_EMAIL_PATTERNS = [
    /@example\.com$/i,
    /@.+\.test$/i,
    /@mailinator\.com$/i,
    /^ratelimit-test/i,
    /@ssds\.po$/i,
];

function isTestEmail(email: string | null): boolean {
    if (!email) return false;
    return TEST_EMAIL_PATTERNS.some(re => re.test(email));
}

export const LeadsPage: React.FC = () => {
    const [leads, setLeads] = useState<Lead[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [filterSource, setFilterSource] = useState<string>('');
    const [filterSearch, setFilterSearch] = useState<string>('');
    const [hideTestLeads, setHideTestLeads] = useState<boolean>(true);
    const [cleanupRunning, setCleanupRunning] = useState(false);
    const [cleanupResult, setCleanupResult] = useState<string | null>(null);

    function refresh() {
        setLoading(true);
        Promise.all([
            fetch('/api/admin/leads?limit=500', { credentials: 'include' })
                .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`)),
            fetch('/api/admin/leads-summary', { credentials: 'include' })
                .then(r => r.ok ? r.json() : null),
        ])
            .then(([leadsData, summaryData]) => {
                setLeads(leadsData.leads || []);
                if (summaryData) setSummary(summaryData);
            })
            .catch(e => setErr(String(e)))
            .finally(() => setLoading(false));
    }

    useEffect(() => { refresh(); }, []);

    const sources = useMemo(() => Array.from(new Set(leads.map(l => l.source))).sort(), [leads]);

    const filtered = useMemo(() => {
        return leads.filter(l => {
            if (hideTestLeads && isTestEmail(l.email)) return false;
            if (filterSource && l.source !== filterSource) return false;
            if (filterSearch) {
                const q = filterSearch.toLowerCase();
                const hay = `${l.email || ''} ${l.name || ''} ${l.phone || ''} ${l.utm_content || ''} ${l.product_slug || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [leads, filterSource, filterSearch, hideTestLeads]);

    const testLeadCount = useMemo(() => leads.filter(l => isTestEmail(l.email)).length, [leads]);

    async function runCleanup() {
        if (!window.confirm(`Usunąć ${testLeadCount} test leadów (example.com / .test / mailinator / ratelimit / ssds.po)?`)) return;
        setCleanupRunning(true);
        setCleanupResult(null);
        try {
            const r = await fetch('/api/admin/leads-summary/cleanup-test', {
                method: 'POST', credentials: 'include',
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
            setCleanupResult(`Usunięto: ${d.deleted_leads} leadów + ${d.deleted_unlocks} unlocks`);
            refresh();
        } catch (e: any) {
            setCleanupResult(`Błąd: ${e.message}`);
        } finally {
            setCleanupRunning(false);
        }
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h1 className="text-2xl font-bold">Leady</h1>
                <div className="flex gap-2 items-center">
                    {testLeadCount > 0 && (
                        <button
                            onClick={runCleanup}
                            disabled={cleanupRunning}
                            className="px-3 py-1.5 text-xs font-semibold bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded-lg disabled:opacity-50"
                        >
                            {cleanupRunning ? 'Czyszczę…' : `🧹 Wyczyść ${testLeadCount} test leadów`}
                        </button>
                    )}
                    <button
                        onClick={refresh}
                        className="px-3 py-1.5 text-xs font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 rounded-lg"
                    >
                        🔄 Odśwież
                    </button>
                    <a
                        href="/api/admin/leads?limit=1000"
                        className="text-xs text-primary-light dark:text-primary-dark hover:underline"
                        target="_blank" rel="noopener noreferrer"
                    >
                        Eksport JSON →
                    </a>
                </div>
            </div>

            {cleanupResult && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${cleanupResult.startsWith('Błąd') ? 'bg-red-500/10 text-red-600' : 'bg-emerald-500/10 text-emerald-600'}`}>
                    {cleanupResult}
                </div>
            )}

            {/* Summary cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <div className="bg-white dark:bg-content-dark rounded-xl p-4 border border-border-light dark:border-border-dark">
                        <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Total</div>
                        <div className="text-2xl font-bold mt-1">{summary.total_leads}</div>
                    </div>
                    <div className="bg-white dark:bg-content-dark rounded-xl p-4 border border-border-light dark:border-border-dark">
                        <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Dzisiaj</div>
                        <div className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">{summary.today}</div>
                    </div>
                    <div className="bg-white dark:bg-content-dark rounded-xl p-4 border border-border-light dark:border-border-dark">
                        <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">7 dni</div>
                        <div className="text-2xl font-bold mt-1">{summary.last_7_days}</div>
                    </div>
                    <div className="bg-white dark:bg-content-dark rounded-xl p-4 border border-border-light dark:border-border-dark">
                        <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">30 dni</div>
                        <div className="text-2xl font-bold mt-1">{summary.last_30_days}</div>
                    </div>
                    <div className="bg-white dark:bg-content-dark rounded-xl p-4 border border-border-light dark:border-border-dark">
                        <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">🔓 Unlocks</div>
                        <div className="text-2xl font-bold mt-1 text-violet-600 dark:text-violet-400">{summary.prompty_unlocks_total}</div>
                    </div>
                </div>
            )}

            {/* Filtry */}
            <div className="flex gap-3 mb-4 flex-wrap items-center">
                <input
                    type="text"
                    placeholder="Szukaj email / imię / utm…"
                    value={filterSearch}
                    onChange={e => setFilterSearch(e.target.value)}
                    className="flex-grow min-w-[180px] max-w-xs px-3 py-1.5 text-sm border border-border-light dark:border-border-dark rounded-lg bg-bkg-light dark:bg-bkg-dark"
                />
                <select
                    value={filterSource}
                    onChange={e => setFilterSource(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-border-light dark:border-border-dark rounded-lg bg-bkg-light dark:bg-bkg-dark"
                >
                    <option value="">Wszystkie źródła</option>
                    {sources.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <label className="text-xs flex items-center gap-1.5 text-text-secondary-light dark:text-text-secondary-dark">
                    <input
                        type="checkbox"
                        checked={hideTestLeads}
                        onChange={e => setHideTestLeads(e.target.checked)}
                    />
                    Ukryj test leady ({testLeadCount})
                </label>
                <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark ml-auto">
                    Pokazano: <b>{filtered.length}</b> / {leads.length}
                </span>
            </div>

            {err && <div className="text-red-500 text-sm mb-4">{err}</div>}
            {loading && <div className="text-text-secondary-light dark:text-text-secondary-dark">Ładowanie…</div>}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Tabela leadów */}
                <div className="lg:col-span-2 overflow-x-auto bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark">
                    <table className="w-full text-sm">
                        <thead className="bg-bkg-light dark:bg-bkg-dark text-left text-xs uppercase tracking-wider">
                            <tr>
                                <th className="px-3 py-2">ID</th>
                                <th className="px-3 py-2">Data</th>
                                <th className="px-3 py-2">Email</th>
                                <th className="px-3 py-2">Source</th>
                                <th className="px-3 py-2">Prompt (utm_content)</th>
                                <th className="px-3 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(l => (
                                <tr key={l.id} className={`border-t border-border-light dark:border-border-dark ${isTestEmail(l.email) ? 'opacity-50 italic' : ''}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{l.id}</td>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap">{l.created_at?.slice(5, 16)}</td>
                                    <td className="px-3 py-2 text-xs">
                                        {l.email || '-'}
                                        {isTestEmail(l.email) && <span className="ml-1 text-[10px] text-amber-600">TEST</span>}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="font-mono">{l.source}</div>
                                        {l.source_detail && <div className="text-[10px] text-text-secondary-light dark:text-text-secondary-dark">{l.source_detail}</div>}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {l.utm_content ? (
                                            <a href={`/prompty/${l.utm_content}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                                                {l.utm_content.slice(0, 40)}…
                                            </a>
                                        ) : '-'}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-500/10 text-blue-600 dark:text-blue-400">
                                            {l.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="p-6 text-center text-text-secondary-light dark:text-text-secondary-dark text-sm">
                            {leads.length === 0 ? 'Brak leadów.' : 'Brak leadów spełniających filtry.'}
                        </div>
                    )}
                </div>

                {/* Sidebar: by_utm_content */}
                <div className="space-y-4">
                    {summary && summary.by_utm_content_30d.length > 0 && (
                        <div className="bg-white dark:bg-content-dark rounded-xl p-4 border border-border-light dark:border-border-dark">
                            <h3 className="font-bold text-sm mb-3">🎯 Top prompty (30d)</h3>
                            <ul className="space-y-2 text-xs">
                                {summary.by_utm_content_30d.slice(0, 10).map(u => (
                                    <li key={u.utm_content} className="flex items-center justify-between gap-2">
                                        <a href={`/prompty/${u.utm_content}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline truncate">
                                            {u.utm_content}
                                        </a>
                                        <span className="font-bold tabular-nums">{u.n}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {summary && summary.by_source_30d.length > 0 && (
                        <div className="bg-white dark:bg-content-dark rounded-xl p-4 border border-border-light dark:border-border-dark">
                            <h3 className="font-bold text-sm mb-3">📥 Źródła (30d)</h3>
                            <ul className="space-y-1.5 text-xs">
                                {summary.by_source_30d.map((s, i) => (
                                    <li key={i} className="flex items-center justify-between gap-2">
                                        <span className="truncate">
                                            <span className="font-mono">{s.source}</span>
                                            {s.source_detail && <span className="text-text-secondary-light dark:text-text-secondary-dark"> / {s.source_detail}</span>}
                                        </span>
                                        <span className="font-bold tabular-nums">{s.n}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
